import { AppError, createError, isAppError, uniqueViolationAppError } from "@crm/errors";
import type { SQL } from "drizzle-orm";
import { Data, Effect } from "effect";

export class CrmQueryRepositoryError extends Data.TaggedError("CrmQueryRepositoryError")<{
  readonly message: string;
  readonly cause?: unknown;
}> {}

export class CrmQueryAppError extends Data.TaggedError("CrmQueryAppError")<{
  readonly error: Error;
}> {}

export class CrmQueryBusinessRuleError extends Data.TaggedError("CrmQueryBusinessRuleError")<{
  readonly message: string;
  readonly details?: unknown;
}> {}

export class CrmQueryNotFoundError extends Data.TaggedError("CrmQueryNotFoundError")<{
  readonly message: string;
  readonly details?: unknown;
}> {}

export class CrmQueryForbiddenError extends Data.TaggedError("CrmQueryForbiddenError")<{
  readonly message: string;
  readonly details?: unknown;
}> {}

export class CrmQueryValidationError extends Data.TaggedError("CrmQueryValidationError")<{
  readonly message: string;
  readonly details?: unknown;
}> {}

export type CrmQueryError =
  | CrmQueryRepositoryError
  | CrmQueryAppError
  | CrmQueryBusinessRuleError
  | CrmQueryNotFoundError
  | CrmQueryForbiddenError
  | CrmQueryValidationError;

export function crmQueryEffect<A>(
  message: string,
  run: () => PromiseLike<A>,
): Effect.Effect<A, CrmQueryError> {
  return Effect.tryPromise({
    try: run,
    catch: (cause) => crmRepositoryError(message, cause),
  });
}

export function crmRepositoryError(message: string, cause: unknown): CrmQueryError {
  if (isCrmQueryError(cause)) {
    return cause;
  }
  if (isAppError(cause)) {
    return new CrmQueryAppError({ error: cause });
  }
  return new CrmQueryRepositoryError({ message, cause });
}

export function crmUniqueViolationError(
  cause: unknown,
  fieldLabels: Record<string, string>,
  fallbackMessage: string,
): CrmQueryError {
  const appError = uniqueViolationAppError(cause, fieldLabels);
  if (appError) {
    return new CrmQueryAppError({ error: appError });
  }
  return crmRepositoryError(fallbackMessage, cause);
}

export function mapCrmQueryEffect<A, R>(
  effect: Effect.Effect<A, CrmQueryError | AppError, R>,
): Effect.Effect<A, Error, R> {
  return effect.pipe(Effect.mapError(mapCrmQueryError));
}

export function requireResolvedScope(scope: SQL | null | undefined): SQL | null {
  if (scope === undefined) {
    throw createError.forbidden("权限不足");
  }
  return scope;
}

function mapCrmQueryError(error: CrmQueryError | AppError): Error {
  if (error instanceof AppError) {
    return error;
  }

  switch (error._tag) {
    case "CrmQueryAppError":
      return error.error;
    case "CrmQueryBusinessRuleError":
      return createError.businessRule(error.message, error.details);
    case "CrmQueryNotFoundError":
      return createError.notFound(error.message, error.details);
    case "CrmQueryForbiddenError":
      return createError.forbidden(error.message, error.details);
    case "CrmQueryValidationError":
      return createError.validation(error.message, error.details);
    case "CrmQueryRepositoryError":
      return createError.database(error.message, { cause: error.cause });
    default: {
      const _exhaustive: never = error;
      return createError.internal("Unhandled CRM query error", {
        error: _exhaustive,
      });
    }
  }
}

function isCrmQueryError(cause: unknown): cause is CrmQueryError {
  return (
    typeof cause === "object" &&
    cause !== null &&
    "_tag" in cause &&
    (cause._tag === "CrmQueryRepositoryError" ||
      cause._tag === "CrmQueryAppError" ||
      cause._tag === "CrmQueryBusinessRuleError" ||
      cause._tag === "CrmQueryNotFoundError" ||
      cause._tag === "CrmQueryForbiddenError" ||
      cause._tag === "CrmQueryValidationError")
  );
}
